import apiService from './ApiService';

interface CodeValidationResult {
  isValid: boolean;
  code?: string;
  description?: string;
  expires_at?: string;
  grants_can_organize?: boolean;
  grants_can_judge?: boolean;
  remaining_uses?: number;
  max_uses?: number;
  error?: string;
  remainingAttempts?: number;
}

interface ValidationCache {
  [code: string]: {
    result: CodeValidationResult;
    timestamp: number;
    ttl: number;
  };
}

class CodeValidationService {
  private cache: ValidationCache = {};
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly RATE_LIMIT_WINDOW = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_ATTEMPTS = 10;
  private validationAttempts: { [ip: string]: { count: number; firstAttempt: number } } = {};

  /**
   * Validate a registration code with rate limiting and caching
   */
  async validateCode(code: string): Promise<CodeValidationResult> {
    // Check rate limiting
    if (!this.checkRateLimit()) {
      return {
        isValid: false,
        error: 'Too many validation attempts. Please try again later.',
        remainingAttempts: 0
      };
    }

    // Clean the code (remove spaces, convert to uppercase)
    const cleanCode = this.cleanCode(code);
    
    if (!cleanCode) {
      return {
        isValid: false,
        error: 'Please enter a valid registration code'
      };
    }

    // Check cache first
    const cachedResult = this.getCachedResult(cleanCode);
    if (cachedResult) {
      return cachedResult;
    }

    try {
      // Call API to validate code
      const response = await apiService.validateRegistrationCode(cleanCode);
      
      const result: CodeValidationResult = {
        isValid: response.valid,
        code: response.code?.code ?? response.data?.code,
        description: response.code?.description ?? response.data?.description,
        expires_at: response.code?.expires_at ?? response.data?.expires_at,
        grants_can_organize: response.code?.grants_can_organize ?? response.data?.grants_can_organize,
        grants_can_judge: response.code?.grants_can_judge ?? response.data?.grants_can_judge,
        remaining_uses: response.code?.remaining_uses ?? response.data?.remaining_uses,
        max_uses: response.code?.max_uses ?? response.data?.max_uses,
        error: response.valid ? undefined : response.message
      };

      // Cache the result
      this.cacheResult(cleanCode, result);
      
      return result;
    } catch (error: any) {
      const result: CodeValidationResult = {
        isValid: false,
        error: error.message || 'Failed to validate registration code'
      };
      
      // Cache negative results for a shorter time to prevent spam
      this.cacheResult(cleanCode, result, 60 * 1000); // 1 minute
      
      return result;
    }
  }

  /**
   * Parse registration code from URL query parameters
   */
  parseCodeFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code') || urlParams.get('invitation_code') || urlParams.get('invite');
    return code ? this.cleanCode(code) : null;
  }

  /**
   * Generate shareable invitation link
   */
  generateInvitationLink(code: string, baseUrl?: string): string {
    const base = baseUrl || window.location.origin;
    const cleanCode = this.cleanCode(code);
    return `${base}/register?code=${encodeURIComponent(cleanCode)}`;
  }

  /**
   * Check if a code format is valid (basic format validation)
   */
  isValidCodeFormat(code: string): boolean {
    const cleanCode = this.cleanCode(code);
    // Built codes follow format: BETA-YYYY-XXXXXXXX
    const codePattern = /^BETA-\d{4}-[A-Z0-9]{8}$/;
    return codePattern.test(cleanCode);
  }

  /**
   * Clean and normalize code input
   */
  private cleanCode(code: string): string {
    return code.trim().toUpperCase().replace(/\s+/g, '');
  }

  /**
   * Check rate limiting for validation attempts
   */
  private checkRateLimit(): boolean {
    const clientId = this.getClientId();
    const now = Date.now();
    
    if (!this.validationAttempts[clientId]) {
      this.validationAttempts[clientId] = { count: 1, firstAttempt: now };
      return true;
    }

    const attempts = this.validationAttempts[clientId];
    
    // Reset if window has passed
    if (now - attempts.firstAttempt > this.RATE_LIMIT_WINDOW) {
      this.validationAttempts[clientId] = { count: 1, firstAttempt: now };
      return true;
    }

    // Check if under limit
    if (attempts.count < this.MAX_ATTEMPTS) {
      attempts.count++;
      return true;
    }

    return false;
  }

  /**
   * Get cached validation result if available and not expired
   */
  private getCachedResult(code: string): CodeValidationResult | null {
    const cached = this.cache[code];
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      delete this.cache[code];
      return null;
    }

    return cached.result;
  }

  /**
   * Cache validation result
   */
  private cacheResult(code: string, result: CodeValidationResult, ttl?: number): void {
    this.cache[code] = {
      result,
      timestamp: Date.now(),
      ttl: ttl || this.CACHE_TTL
    };

    // Clean old cache entries
    this.cleanCache();
  }

  /**
   * Clean expired cache entries
   */
  private cleanCache(): void {
    const now = Date.now();
    Object.keys(this.cache).forEach(code => {
      const cached = this.cache[code];
      if (now - cached.timestamp > cached.ttl) {
        delete this.cache[code];
      }
    });
  }

  /**
   * Get a client identifier for rate limiting
   */
  private getClientId(): string {
    // In a real app, you might use IP address or session ID
    // For now, use a browser fingerprint
    return 'browser_' + (window.navigator.userAgent + window.navigator.language).slice(0, 50);
  }

  /**
   * Clear all cached results
   */
  clearCache(): void {
    this.cache = {};
  }

  /**
   * Get remaining validation attempts
   */
  getRemainingAttempts(): number {
    const clientId = this.getClientId();
    const attempts = this.validationAttempts[clientId];
    
    if (!attempts) return this.MAX_ATTEMPTS;
    
    const now = Date.now();
    if (now - attempts.firstAttempt > this.RATE_LIMIT_WINDOW) {
      return this.MAX_ATTEMPTS;
    }
    
    return Math.max(0, this.MAX_ATTEMPTS - attempts.count);
  }
}

const codeValidationService = new CodeValidationService();
export default codeValidationService; 